import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Stack, Card, Group, Text, TextInput, Textarea, Button, ActionIcon, Tooltip, Loader } from '@mantine/core';
import PageHeader from '@/components/common/PageHeader';
import { useAtom, useSetAtom } from 'jotai';
import { activePageAtom, editingMineIdAtom } from '@/stores/atoms/uiAtoms';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { notifications } from '@mantine/notifications';
import { mineGamesAtom } from '@/stores/atoms/mineAtoms';
import { confirmDialog } from '@/utils/confirm';
// Removed overlay auto-open dependencies
import { httpFetch } from '@/renderer/utils/http';
import { getApiBase } from '@/renderer/utils/session';
import { getStoredAuthToken } from '@/utils/userApi';
// import { languageAtom } from '@/stores/atoms/i18nAtoms';
import { IconPlus, IconTrash, IconPhoto } from '@tabler/icons-react';
import SafeImage from '@/components/common/SafeImage';
import { getImageApiBase } from '@/renderer/utils/session';
import { modals } from '@mantine/modals';
const CreateNovel = () => {
    const setActivePage = useSetAtom(activePageAtom);
    const [authUser] = useAtom(authUserAtom);
    const setAuthModalOpen = useSetAtom(authModalOpenAtom);
    // Draft editing support
    const [editingId, setEditingId] = useAtom(editingMineIdAtom);
    const [mine, setMine] = useAtom(mineGamesAtom);
    // Overlay auto-open removed
    const [title, setTitle] = React.useState('');
    const [persona, setPersona] = React.useState('');
    const [opening, setOpening] = React.useState('');
    const [features, setFeatures] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [imageUrl, setImageUrl] = React.useState(undefined);
    const fileRef = React.useRef(null);
    const [coverUploading, setCoverUploading] = React.useState(false);
    const addFeature = () => setFeatures((list) => [...list, { title: '', text: '' }]);
    const removeFeature = (idx) => setFeatures((list) => list.filter((_, i) => i !== idx));
    const updateFeature = (idx, patch) => setFeatures((list) => list.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    // Ensure a draft exists when entering this page
    const [isEditingExisting, setIsEditingExisting] = React.useState(false);
    React.useEffect(() => {
        // If already editing an existing draft, hydrate form
        const found = (editingId && (mine || []).find((m) => m.id === editingId)) || null;
        if (found) {
            setIsEditingExisting(true);
            setTitle(found.title || '');
            setPersona(found.content || '');
            setOpening(found.prologue || '');
            setImageUrl(found.imageUrl || undefined);
            return;
        }
        // Create a new draft entry on first open
        const id = `draft_${Date.now()}`;
        const now = Date.now();
        const draft = {
            id,
            title: '',
            content: '',
            prologue: '',
            createdAt: now,
            updatedAt: now,
            isDraft: true,
            imageUrl: undefined,
        };
        setMine([draft, ...(mine || [])]);
        setEditingId(id);
    }, []);
    // Real-time save to draft when fields change
    React.useEffect(() => {
        if (!editingId)
            return;
        setMine((prev) => {
            const list = prev || [];
            const idx = list.findIndex((m) => m.id === editingId);
            const now = Date.now();
            if (idx === -1) {
                return [{ id: editingId, title, content: persona, prologue: opening, createdAt: now, updatedAt: now, isDraft: true, imageUrl }, ...list];
            }
            const cur = list[idx];
            const next = { ...cur, title, content: persona, prologue: opening, imageUrl, updatedAt: now };
            const copy = list.slice();
            copy[idx] = next;
            return copy;
        });
    }, [editingId, title, persona, opening, imageUrl]);
    const onSubmit = async () => {
        if (!authUser) {
            setAuthModalOpen(true);
            return;
        }
        if (!title.trim() || !persona.trim() || !opening.trim()) {
            notifications.show({ color: 'red', title: '信息不完整', message: '请填写标题、角色人设与开场白', withCloseButton: true });
            return;
        }
        setSubmitting(true);
        try {
            // 1) Submit to server
            const token = getStoredAuthToken();
            if (!token) {
                setAuthModalOpen(true);
                return;
            }
            const base = getApiBase();
            const featuresPayload = (features || []).filter((f) => (f.title?.trim() || f.text?.trim())).map((f, idx) => ({
                id: `entry-${idx + 1}`,
                name: (f.title || '').trim() || `entry-${idx + 1}`,
                content: (f.text || '').trim(),
            }));
            const payload = {
                id: undefined,
                title: title.trim(),
                prompt: persona.trim(),
                prologue: opening.trim(),
                image_url: imageUrl || undefined,
                features: featuresPayload,
            };
            try {
                const res = await httpFetch(`${base}/api/commit_game`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload),
                    timeoutMs: 20000,
                });
                let data = undefined;
                let text;
                try {
                    data = await res.json();
                }
                catch {
                    try {
                        text = await res.text();
                    }
                    catch { }
                }
                if (!res.ok || (data && (data.success === false || data.error))) {
                    const msg = String((data?.error || text || `HTTP ${res.status}`));
                    throw new Error(msg);
                }
                const serverId = String(data?.id || data?.adventure_id || '');
                if (serverId) {
                    // Mark local draft as submitted
                    if (editingId) {
                        const now = Date.now();
                        const sig = JSON.stringify([title.trim(), persona.trim(), opening.trim(), imageUrl || '']);
                        setMine((prev) => (prev || []).map((m) => m.id === editingId ? { ...m, isDraft: false, advId: serverId, updatedAt: now, lastSubmittedAt: now, lastSubmittedSig: sig } : m));
                    }
                    notifications.show({ color: 'green', title: '已提交', message: '小说设定已保存到服务器', withCloseButton: true });
                }
            }
            catch (e) {
                modals.open({ title: '提交失败', centered: true, withCloseButton: true, children: (_jsx("div", { children: _jsx(Text, { size: "sm", c: "dimmed", children: String(e?.message || '提交到服务器失败') }) })) });
                return;
            }
            // 提交完成后直接返回列表页或“我的”页面
            setActivePage('mine');
        }
        finally {
            setSubmitting(false);
        }
    };
    const uploadImageDataUrl = async (dataUrl, filename) => {
        const api = getImageApiBase();
        const res = await httpFetch(`${api}/api/images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: dataUrl, filename }),
            timeoutMs: 20000,
        });
        if (!res.ok)
            throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || json.success === false)
            throw new Error(String(json?.error || '上传失败'));
        return json.image_url || json.url;
    };
    const pickCover = async (file) => {
        if (!file)
            return;
        try {
            setCoverUploading(true);
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            const url = await uploadImageDataUrl(dataUrl, file.name);
            setImageUrl(url);
            notifications.show({ color: 'green', title: '封面已上传', message: '已设置为本作品封面', withCloseButton: true });
        }
        catch (e) {
            notifications.show({ color: 'red', title: '上传失败', message: String(e?.message || '封面上传失败'), withCloseButton: true });
        }
        finally {
            setCoverUploading(false);
        }
    };
    const pageTitle = isEditingExisting ? '编辑小说' : '创建小说';
    return (_jsxs(Stack, { p: "sm", gap: "sm", style: { height: '100%', overflow: 'hidden' }, children: [_jsx(PageHeader, { title: pageTitle, onBack: () => setActivePage(isEditingExisting ? 'mine' : 'home') }), _jsx("div", { style: { flex: 1, minHeight: 0, overflow: 'auto' }, children: _jsxs(Stack, { p: "sm", gap: "sm", children: [_jsx(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: _jsxs(Stack, { gap: "sm", children: [_jsx(TextInput, { label: "\u6807\u9898", placeholder: "\u4F8B\u5982\uFF1A\u96E8\u591C\u8FF7\u57CE", value: title, onChange: (e) => setTitle(e.currentTarget.value) }), _jsx(Textarea, { label: "\u4EBA\u7269&\u80CC\u666F", placeholder: "\u4EBA\u7269\u4E0E\u6545\u4E8B\u80CC\u666F\u63CF\u8FF0", minRows: 4, value: persona, onChange: (e) => setPersona(e.currentTarget.value) }), _jsx(Textarea, { label: "\u5F00\u573A\u767D", placeholder: "\u4F5C\u4E3A\u6545\u4E8B\u5F00\u7BC7\u7684\u6BB5\u843D", minRows: 3, value: opening, onChange: (e) => setOpening(e.currentTarget.value) }), _jsxs("div", { children: [_jsxs(Group, { justify: "space-between", align: "center", mb: 6, children: [_jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: "\u5C01\u9762" }), _jsxs(Group, { gap: 8, children: [imageUrl && (_jsx(Button, { color: "red", variant: "subtle", leftSection: _jsx(IconTrash, { size: 14 }), onClick: () => setImageUrl(undefined), children: "\u79FB\u9664" })), _jsx(Button, { leftSection: _jsx(IconPhoto, { size: 14 }), variant: "light", onClick: () => fileRef.current?.click(), disabled: coverUploading, children: coverUploading ? '上传中…' : '上传封面' }), _jsx("input", { ref: fileRef, type: "file", accept: "image/*", style: { display: 'none' }, onChange: (e) => {
                                                                    const f = e.currentTarget.files?.[0];
                                                                    e.currentTarget.value = '';
                                                                    void pickCover(f || null);
                                                                } })] })] }), _jsxs("div", { style: { position: 'relative', maxWidth: 420 }, children: [imageUrl ? (_jsx(Card, { withBorder: true, radius: "md", p: 0, style: { overflow: 'hidden' }, children: _jsx(SafeImage, { src: imageUrl, alt: "\u5C01\u9762", height: 220, fit: "cover" }) })) : (_jsx(Text, { size: "xs", c: "dimmed", children: "\u5EFA\u8BAE\u4E0A\u4F20\u6E05\u6670\u7684\u6A2A\u56FE\u5C01\u9762\uFF08\u5C06\u7528\u4E8E\u5C55\u793A\u548C\u804A\u5929\u80CC\u666F\uFF09\u3002" })), coverUploading && (_jsx("div", { style: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', borderRadius: 8 }, children: _jsx(Loader, { color: "white" }) }))] })] })] }) }), _jsxs(Card, { withBorder: true, radius: "md", p: "md", className: "card-soft", children: [_jsxs(Group, { justify: "space-between", align: "center", children: [_jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: "\u8BCD\u6761" }), _jsx(Tooltip, { label: "\u6DFB\u52A0\u8BCD\u6761", children: _jsx(ActionIcon, { variant: "light", onClick: addFeature, children: _jsx(IconPlus, { size: 16 }) }) })] }), _jsxs(Stack, { gap: "sm", mt: "sm", children: [features.length === 0 && (_jsx(Text, { size: "sm", c: "dimmed", children: "\u6DFB\u52A0\u82E5\u5E72\u8BCD\u6761\u4EE5\u8865\u5145\u4E16\u754C\u89C2/\u89D2\u8272/\u5267\u60C5\u8BBE\u5B9A\uFF08\u53EF\u9009\uFF09\u3002" })), features.map((f, idx) => (_jsxs(Card, { withBorder: true, radius: "md", p: "sm", className: "card-soft", children: [_jsxs(Group, { justify: "space-between", align: "center", mb: 6, children: [_jsxs(Text, { fw: 600, size: "sm", style: { color: 'var(--app-text-primary)' }, children: ["\u8BCD\u6761 ", idx + 1] }), _jsx(ActionIcon, { color: "red", variant: "subtle", onClick: () => removeFeature(idx), "aria-label": "\u5220\u9664\u8BCD\u6761", children: _jsx(IconTrash, { size: 14 }) })] }), _jsx(TextInput, { placeholder: "\u8BCD\u6761\u6807\u9898", value: f.title, onChange: (e) => updateFeature(idx, { title: e.currentTarget.value }), mb: 6 }), _jsx(Textarea, { placeholder: "\u8BCD\u6761\u5185\u5BB9", minRows: 2, value: f.text, onChange: (e) => updateFeature(idx, { text: e.currentTarget.value }) })] }, idx)))] })] }), _jsxs(Group, { justify: "space-between", align: "center", children: [_jsx("div", { children: isEditingExisting && editingId && (_jsx(Button, { color: "red", variant: "subtle", onClick: async () => {
                                            const ok = await confirmDialog({ title: '彻底删除', message: '确定删除该作品？此操作不可恢复。', labels: { confirm: '删除', cancel: '取消' } });
                                            if (!ok)
                                                return;
                                            const cur = (mine || []).find((m) => m.id === editingId);
                                            const advId = cur?.advId;
                                            if (advId) {
                                                try {
                                                    const token = getStoredAuthToken();
                                                    if (!token) {
                                                        setAuthModalOpen(true);
                                                        return;
                                                    }
                                                    const base = getApiBase();
                                                    const res = await httpFetch(`${base}/api/commit_game`, {
                                                        method: 'DELETE',
                                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                        body: JSON.stringify({ id: advId }),
                                                        timeoutMs: 15000,
                                                    });
                                                    if (!res.ok) {
                                                        const body = await res.text().catch(() => '');
                                                        throw new Error(body || `HTTP ${res.status}`);
                                                    }
                                                }
                                                catch (e) {
                                                    notifications.show({ color: 'red', title: '删除失败', message: String(e?.message || '服务器删除失败'), withCloseButton: true });
                                                    return;
                                                }
                                            }
                                            // Remove local
                                            setMine((prev) => (prev || []).filter((m) => m.id !== editingId));
                                            setEditingId(null);
                                            setActivePage('mine');
                                        }, children: "\u5F7B\u5E95\u5220\u9664" })) }), _jsxs(Group, { gap: "sm", children: [_jsx(Button, { variant: "light", onClick: () => setActivePage(isEditingExisting ? 'mine' : 'home'), children: "\u53D6\u6D88" }), _jsx(Button, { onClick: onSubmit, loading: submitting, children: "\u63D0\u4EA4" })] })] })] }) })] }));
};
export default CreateNovel;
